import React from 'react';
import * as Yup from 'yup';
import { Box, Button, FormHelperText, Grid, Input, TextField } from '@mui/material';
import { Field, FieldProps, Form, Formik, FormikProps } from 'formik';
import { CourseResource as CourseResourceModel } from '@juno/client-api/model';
import { FILE_UPLOAD_TYPES } from '@juno/constants';
import {
  DrawerFooter,
  DrawerHeader,
  JunoImageUpload,
  JunoSpin,
  RightExpandableDrawer,
} from '@juno/ui';
import { optimizeImage, readImageFile, validateSelectedFileSize } from '@juno/utils';

interface FormDialogProps {
  isLoading: boolean;
  item: CourseResourceModel | undefined;
  open: boolean;
  onSave: (resource: PayloadProps | undefined) => void;
  onClose: () => void;
  onDelete: () => void;
  isSaving: boolean;
}

export interface ResourceProps {
  title?: string;
  description?: string;
  icon?: string;
  iconUrl?: string;
  download?: {
    url?: string;
    filetype?: string;
  };
}

export interface PayloadProps {
  resource?: any; // TODO: find a more typescripty way to key/value pair by string
  file?: File | null;
  iconFile?: File;
  iconUrl?: string;
}

const validationSchema = Yup.object({
  resource: Yup.object({
    title: Yup.string()
      .required('Title is required')
      .max(67, 'Title must be less than 67 characters'),
    description: Yup.string()
      .required('Description is required')
      .max(78, 'Description must be less than 78 characters'),
  }),
});

const FormDialog: React.FC<FormDialogProps> = ({
  isLoading,
  item,
  open,
  onClose,
  onSave,
  onDelete,
  isSaving,
}) => {
  const initialValues: PayloadProps = {
    resource: item || {},
    file: undefined,
    iconFile: undefined,
    iconUrl: undefined,
  };

  const validateForm = (values: PayloadProps, isValid: boolean, dirty: boolean): boolean => {
    return isValid && dirty && (item ? true : !!values.file);
  };

  if (isLoading) return <JunoSpin />;
  return (
    <RightExpandableDrawer open={open} onClose={onClose}>
      <DrawerHeader title={'Edit Resource'} />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        validateOnBlur
        validateOnChange
        onSubmit={(values) => onSave(values)}
        enableReinitialize
      >
        {(formikProps: FormikProps<PayloadProps>) => {
          const { values, isValid, dirty, handleChange, handleBlur, setFieldValue } = formikProps;

          const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (validateSelectedFileSize(file)) {
              setFieldValue('file', file);
            } else {
              setFieldValue('file', null);
            }
          };

          const handleImageUpload = (file: File | null) => {
            if (!file) {
              setFieldValue('iconFile', null);
              setFieldValue('iconUrl', null);
              setFieldValue('resource.icon', null);
            } else {
              readImageFile(file, (b64Str) => {
                setFieldValue('iconFile', file);
                setFieldValue('iconUrl', b64Str);
                setFieldValue('resource.icon', b64Str);
              });
            }
          };

          const isFormValid = validateForm(values, isValid, dirty);

          return (
            <Form>
              <Grid container spacing={1}>
                <Grid item xs={6} sm={7}>
                  <Field name='resource.title'>
                    {({ field, meta }: FieldProps) => (
                      <>
                        <TextField
                          disabled={isSaving}
                          id='resource.title'
                          label='Title'
                          fullWidth
                          margin='dense'
                          {...field}
                          value={field.value || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={meta.touched && Boolean(meta.error)}
                        />
                        <FormHelperText error>{meta.touched && meta.error}</FormHelperText>
                      </>
                    )}
                  </Field>
                  <Field name='resource.description'>
                    {({ field, meta }: FieldProps) => (
                      <>
                        <TextField
                          disabled={isSaving}
                          id='resource.description'
                          label='Description'
                          fullWidth
                          margin='dense'
                          {...field}
                          value={field.value || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={meta.touched && Boolean(meta.error)}
                        />
                        <FormHelperText error>{meta.touched && meta.error}</FormHelperText>
                      </>
                    )}
                  </Field>
                </Grid>
                <Grid item xs={6} sm={5}>
                  <Box sx={{ mt: 1 }}>
                    <JunoImageUpload
                      style={{ aspectRatio: '16/9' }}
                      src={optimizeImage(200, values.resource?.iconUrl || values.resource?.icon)}
                      srcUrl={values.resource?.iconUrl || values.resource?.icon || values.iconUrl}
                      onFileSelected={handleImageUpload}
                      imageType='Resource'
                    />
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ display: 'flex' }}>
                <TextField
                  disabled
                  id='url'
                  name='url'
                  label='File'
                  fullWidth
                  margin='dense'
                  variant='standard'
                  value={values.file?.name || values.resource?.download?.url || ''}
                />
                <TextField
                  disabled
                  id='filetype'
                  name='filetype'
                  label='Type'
                  margin='dense'
                  sx={{ ml: 1 }}
                  variant='standard'
                  value={values.file?.type || values.resource?.download?.filetype || ''}
                />
                <Button
                  variant='contained'
                  sx={{ width: '270px', ml: 1, mt: 1, mb: 0.5 }}
                  component='label'
                  disabled={isSaving}
                >
                  Upload File
                  <Input
                    type='file'
                    inputProps={{
                      accept: FILE_UPLOAD_TYPES.join(','),
                      multiple: true,
                    }}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </Button>
              </Box>
              {!!item && (
                <Button
                  disabled={isSaving}
                  size='small'
                  variant='contained'
                  color='error'
                  sx={{ mt: 2 }}
                  onClick={onDelete}
                >
                  Delete
                </Button>
              )}

              <DrawerFooter
                onCancel={onClose}
                onSubmit={formikProps.handleSubmit}
                disabled={!isFormValid}
                submitting={isSaving}
              />
            </Form>
          );
        }}
      </Formik>
    </RightExpandableDrawer>
  );
};

export default FormDialog;
